"use client";

import { useEffect, useRef, useState } from "react";
import Image from "next/image";
import { usePathname } from "next/navigation";
import XIcon from "@/icons/actions/x-thin.svg";
import { cn } from "~/lib/utils";
import { Paragraph } from "~/componentsDS/Typography/Paragraph";
import { usePeopleWatching } from "./usePeopleWatching";

const SHOW_DELAY_MS = 2000;
const TRANSITION_DURATION_MS = 800;

const PeopleWatchingWidget = () => {
    const pathname = usePathname();
    const {
        viewingCount,
        purchasedCount,
        pageConfig,
        isEnabled,
        dismiss,
    } = usePeopleWatching();

    const [isVisible, setIsVisible] = useState(false);
    const [render, setRender] = useState(false);

    useEffect(() => {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setIsVisible(false);

        if (!isEnabled) return;

        const timer = setTimeout(() => setIsVisible(true), SHOW_DELAY_MS);
        return () => clearTimeout(timer);
    }, [isEnabled, pathname]);

    const dismissTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setRender(true);

        return () => {
            if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current);
        };
    }, []);

    if (!isEnabled || !pageConfig) return null;

    const handleDismiss = () => {
        if (dismissTimerRef.current) return; // Already dismissing
        setIsVisible(false);
        dismissTimerRef.current = setTimeout(() => {
            dismissTimerRef.current = null;
            dismiss();
        }, TRANSITION_DURATION_MS);
    };

    if (!render) {
        return null
    }

    return (
        <div
            className={cn(
                "fixed bottom-5 left-6 right-6 md:left-5 md:right-auto z-50 md:w-[280px]",
                " bg-white rounded-xl",
                "shadow-[0px_20px_25px_-5px_#0000000D,0px_8px_10px_-6px_#0000000D]",
                "flex items-start gap-3 p-2 md:p-2",
                "transition-[transform,opacity] ease-out",
                isVisible
                    ? "translate-y-0 opacity-100 pointer-events-auto"
                    : "translate-y-2 opacity-0 pointer-events-none"
            )}
            style={{ transitionDuration: `${TRANSITION_DURATION_MS}ms` }}
        >
            <div className="relative shrink-0 w-[80px] h-[80px] md:w-[72px] md:h-[72px]  rounded-[4px] bg-[#F5F7FB]
                        before:content-[''] before:absolute before:top-0 before:left-0 before:z-10
                        before:w-3 before:h-3 before:bg-success300 before:rounded-full">
                <Image
                    src={pageConfig.productImage}
                    alt={pageConfig.productName}
                    fill
                    className="object-cover"
                    sizes="100px"
                />
            </div>

            <div className="flex flex-col gap-2 min-w-0 max-w-[210px] md:max-w-[140px] pt-2">
                <Paragraph size="md" sizes={{ md: "sm" }} weight="400" className="md:leading-[1.3] max-w-[237px]">
                    <strong>{viewingCount} people</strong>
                    {" are viewing "} <br />
                    <strong>{pageConfig.productName}</strong>
                    {" right now."}
                </Paragraph>

                <Paragraph size="sm" sizes={{ md: "xs" }} weight="400" className="text-trueBlack/50 leading-3">
                    {purchasedCount} women bought it today
                </Paragraph>
            </div>

            <button
                type="button"
                onClick={handleDismiss}
                aria-label="Dismiss"
                className="absolute top-2 right-2
                           w-6 h-6 p-1 rounded-full flex items-center justify-center
                         bg-primary100"
            >
                <XIcon fill="#183690" />
            </button>
        </div>
    );
};

export default PeopleWatchingWidget;
